import React from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import TabPanelShell from '~/components/Offers/Detail/OfferBuilder/shared/TabPanelShell';

import { useOverridesContext } from '../../PricingToolSessionContext';

import { validateOverrides } from './overridesValidation';
import type { OverrideBasis, OverrideOption } from './types';

const OPTION_VALUES: Array<OverrideOption> = [1, 2, 3];
const BASIS_OPTIONS: Array<{ value: OverrideBasis; label: string }> = [
  { value: 'vendor_revenue', label: 'Vendor revenue' },
  { value: 'room_nights', label: 'Room nights' },
];

function toNumber(value: string): number {
  if (value === '') {
    return 0;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export default function OverridesTab() {
  const { overrides, setOption, setPreferredOption, setBasis, addTier, updateTier, removeTier } = useOverridesContext();
  const errors = validateOverrides(overrides);

  return (
    <TabPanelShell
      subtitle="Per-Option override tiers are kept in this session until Save Session is wired."
      title="Overrides"
    >
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <Box>
            <Typography gutterBottom variant="subtitle2">
              Option
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={overrides.selectedOption}
              onChange={(_, value: OverrideOption | null) => value && setOption(value)}
            >
              {OPTION_VALUES.map((option) => (
                <ToggleButton key={option} value={option}>
                  Option {option}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Box>
            <Typography gutterBottom variant="subtitle2">
              Basis
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={overrides.basis}
              onChange={(_, value: OverrideBasis | null) => value && setBasis(value)}
            >
              {BASIS_OPTIONS.map((option) => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <Box>
            <Typography gutterBottom variant="subtitle2">
              Preferred option
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={overrides.preferredOption}
              onChange={(_, value: OverrideOption | null) => setPreferredOption(value)}
            >
              {OPTION_VALUES.map((option) => (
                <ToggleButton key={option} value={option}>
                  Option {option}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Typography color="text.secondary" variant="caption">
              Which option is preferred for commercial approval (optional).
            </Typography>
          </Box>
        </Stack>

        {errors.length > 0 && (
          <Alert severity="error" variant="outlined">
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {errors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </Box>
          </Alert>
        )}

        <Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>From (optional)</TableCell>
                <TableCell>To</TableCell>
                <TableCell>Comms</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {overrides.tiers.map((tier, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={tier.from ?? ''}
                      onChange={(event) =>
                        updateTier(index, {
                          from: event.target.value === '' ? undefined : toNumber(event.target.value),
                        })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={tier.to ?? ''}
                      onChange={(event) =>
                        updateTier(index, {
                          to: event.target.value === '' ? undefined : toNumber(event.target.value),
                        })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={tier.comms ?? ''}
                      onChange={(event) =>
                        updateTier(index, {
                          comms: event.target.value === '' ? undefined : toNumber(event.target.value),
                        })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton aria-label="Remove tier" onClick={() => removeTier(index)} size="small">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Button onClick={addTier} size="small" variant="outlined">
          Add tier
        </Button>
      </Stack>
    </TabPanelShell>
  );
}
